import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import CardHeader from '../../components/CardHeader';
import ArrowRightIcon from '../../assets/icons/ArrowRightIcon';
import UpLoadingBar from '../../components/Common/UpLoadingBar';
import UpInput from '../../components/Common/UpInput';
import UpSelectDropdown from '../../components/Common/UpSelectDropdown';
import LocalStorageHelper from '../../helpers/localStorageHelpers';
import UpLoadingCover from '../../components/Common/UpLoadingCover';
import PlaceholderBiz from '../../assets/images/placeholder-biz.svg';
import AppAccessRequired from './AppAccessRequired';
import usePlatformRedirect from '../../helpers/usePlatformRedirect';
import './style.scss';

function SelectBusiness() {
  const navigate = useNavigate();
  const appLogin = LocalStorageHelper.getAppLogin();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('active');

  const allBizData = LocalStorageHelper.getBizData();
  const redirectHost = LocalStorageHelper.getRedirectHost();
  const filterOptions = [
    { value: 'active', label: 'Active' },
    { value: 'deactivated', label: 'Deactivated' },
  ];

  const {
    isLoading,
    redirect: callRedirectPlatform,
    atlasEnvPicker,
  } = usePlatformRedirect();

  const filteredBusinessList = useMemo(() => {
    const filtered = (allBizData || []).filter(biz => {
      if (filterType === 'active') {
        return !biz?.is_login_restricted;
      }
      return biz?.is_login_restricted;
    });

    const sorted = filtered.sort((a, b) => {
      const nameA = (a?.biz_name || a?.appsbiz?.[0]?.app_biz_name || '').toLowerCase();
      const nameB = (b?.biz_name || b?.appsbiz?.[0]?.app_biz_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return sorted;
  }, [allBizData, filterType]);

  const fuse = useMemo(
    () =>
      new Fuse(filteredBusinessList || [], {
        keys: ['biz_name', 'appsbiz.app_biz_name'],
        threshold: 0.4,
        includeScore: true,
        useExtendedSearch: true,
      }),
    [filteredBusinessList],
  );

  // Filter businesses with exact match and fuzzy search
  const { filteredBizData: filteredData, hasExactMatch } = useMemo(() => {
    let filtered = filteredBusinessList || [];
    let exactMatch = false;

    if (searchTerm.trim()) {
      // First try exact match
      const exactMatches = filtered.filter(biz => {
        const bizName = biz?.biz_name || biz?.appsbiz?.[0]?.app_biz_name || '';
        return bizName.toLowerCase().includes(searchTerm.toLowerCase());
      });

      if (exactMatches.length > 0) {
        exactMatch = true;
        filtered = exactMatches;
      } else {
        const results = fuse.search(searchTerm);
        filtered = results.map(result => result.item);
      }
    }

    return { filteredBizData: filtered, hasExactMatch: exactMatch };
  }, [filteredBusinessList, searchTerm, fuse]);

  function redirectToBizPlatform(bizData) {
    if (bizData?.is_login_restricted) {
      navigate('/login-restricted', {
        state: {
          bizData,
          reason_code: bizData?.login_restriction_reason || 'billing',
        },
      });
      return;
    }

    const availablePlatforms = [];

    (bizData?.appsbiz || []).forEach(biz => {
      availablePlatforms.push(biz?.app_name);
    });

    if (availablePlatforms?.length === 1) {
      callRedirectPlatform({
        appType: availablePlatforms?.[0],
        params: {
          biz_id: bizData?.biz_id,
          app_name: availablePlatforms?.[0],
          app_biz_id: bizData?.appsbiz?.[0]?.app_biz_id,
          redirect_host: redirectHost,
        },
      });
      return;
    }

    navigate(`/business/${bizData?.biz_id}`);
  }

  if (
    allBizData?.length === 0 &&
    (appLogin === 'prime' || appLogin === 'atlas')
  ) {
    return <AppAccessRequired appLogin={appLogin} />;
  }

  const renderFilterAndSearch = () => {
    return (
      <div className="flex items-center gap-4 pt-4 pb-6 border-b border-neutral-grey">
        <UpSelectDropdown
          value={filterType}
          onValueChange={setFilterType}
          options={filterOptions}
          placeholder="Select filter"
          wrapperClass="business-active-filter"
        />
        <UpInput
          name="search"
          value={searchTerm}
          placeholder="Search"
          onChange={setSearchTerm}
          wrapperClass="business-search-input"
          showSearchIcon={true}
          onClear={() => setSearchTerm('')}
        />
      </div>
    );
  };

  const renderSubText = () => {
    const hasResults = filteredData?.length > 0;
    const isSearching = searchTerm.trim().length > 0;
    const filterLabel = filterType === 'active' ? 'active' : 'deactivated';

    if (isSearching && !hasResults) {
      return (
        // No results at all
        <div className="mb-3 mt-4">
          <p className="text-base text-up-clr-light-grey mb-1">
            There are no matching results for{' '}
            <span className="font-semibold text-up-clr-dark-grey">
              {searchTerm}
            </span>
          </p>
          <p className="text-sm text-gray-400">
            Adjust your search query to try again.
          </p>
        </div>
      );
    }
    if (isSearching && !hasExactMatch && hasResults) {
      return (
        // Fuzzy/related results only
        <div className="mb-3 mt-4">
          <p className="text-base text-up-clr-light-grey mb-1">
            There are no matching results for{' '}
            <span className="font-semibold text-up-clr-dark-grey">
              {searchTerm}
            </span>
          </p>
          <p className="font-semibold text-sm text-up-clr-light-grey">
            Showing related results
          </p>
        </div>
      );
    }
    if (isSearching) {
      return (
        // Exact match results
        <p className="font-semibold text-sm mb-3 mt-4 text-up-clr-light-grey">
          {filteredData?.length}
          {filteredData?.length === 1 ? ' result' : ' results'} for{' '}
          <span className="text-up-clr-light-grey">{searchTerm}</span>
        </p>
      );
    }
    return (
      // Default - not searching
      <p className="font-semibold text-sm mb-3 mt-4 text-up-clr-light-grey">
        Your {filterLabel} businesses
        <span className="text-up-clr-light-grey">{` (${filteredData?.length})`}</span>
      </p>
    );
  };

  return (
    <div>
      {atlasEnvPicker}
      <UpLoadingBar loading={isLoading} />
      <div className="light-view-wrapper relative !px-6 !py-6 max-h-[686px]">
        <UpLoadingCover loading={isLoading} />
        <div>
          <CardHeader title="Select a business" wrapperClass="pb-4" />
          {renderFilterAndSearch()}
          <div aria-live="polite" aria-atomic="true">
            {renderSubText()}
          </div>
          <ul
            role="list"
            aria-label="Business list"
            className="max-h-[450px] overflow-y-auto business-list-container p-1">
            {filteredData?.map((bizData, idx) => {
              return (
                <li
                  className="max-w-[496px]"
                  key={`${bizData?.biz_id}-${idx}`}>
                  <BusinessCard
                    bizData={bizData}
                    redirectToBizPlatform={redirectToBizPlatform}
                  />
                  {idx < filteredData?.length - 1 && (
                    <div className="border-t border-neutral-grey" aria-hidden="true" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function BusinessCard({ bizData, redirectToBizPlatform }) {
  const biz = bizData?.appsbiz?.[0] || {};
  const allRoles = bizData?.appsbiz?.reduce((acc, appBiz) => {
    if (appBiz?.roles_name?.length > 0) {
      acc.push(...appBiz.roles_name);
    }
    return acc;
  }, []);

  const bizName = bizData?.biz_name || biz?.app_biz_name;
  const isDeactivated = bizData?.is_login_restricted;

  const ariaLabel = [
    bizName,
    isDeactivated ? 'Deactivated' : null,
    allRoles?.length ? `Roles: ${allRoles.join(', ')}` : null,
  ]
    .filter(Boolean)
    .join('. ');

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      redirectToBizPlatform(bizData);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={() => redirectToBizPlatform(bizData)}
      onKeyDown={handleKeyDown}
      className="py-[22px] px-3 flex items-center cursor-pointer transition-colors duration-500 text-up-clr-neutral-grey hover:text-up-clr-tinted-grey focus:outline-none focus-visible:ring-2 focus-visible:ring-up-clr-primary focus-visible:ring-offset-2 rounded">
      <div
        aria-hidden="true"
        className={`w-12 h-12 flex-shrink-0 rounded-lg border border-gray-200 overflow-hidden bg-white flex items-center justify-center ${
          isDeactivated ? 'opacity-50' : ''
        }`}>
        <img
          src={bizData?.biz_logo || biz?.app_biz_logo || PlaceholderBiz}
          alt=""
          className="w-full h-full object-contain"
        />
      </div>
      <div style={{ maxWidth: 300 }} className="ml-4 mr-auto">
        <p
          aria-hidden="true"
          className={`text-lg font-semibold leading-6 ${
            isDeactivated ? 'text-up-clr-light-grey' : 'text-up-clr-black'
          }`}>
          {bizName}
        </p>
        <p
          aria-hidden="true"
          className={`text-sm ${
            isDeactivated ? 'text-up-clr-light-grey' : 'text-up-clr-dark-grey'
          }`}>
          {(allRoles || []).join(', ')}
        </p>
      </div>
      <div className="flex items-center gap-[8px]">
        {isDeactivated && (
          <span
            aria-hidden="true"
            className="w-[88px] h-[26px] px-[8px] py-[2px] text-sm font-semibold bg-[#FFE5E5] text-[#B02B2B] rounded-[2px] flex items-center justify-center">
            Deactivated
          </span>
        )}
        <div aria-hidden="true" className="select-none">
          <ArrowRightIcon />
        </div>
      </div>
    </div>
  );
}

export default SelectBusiness;
